<script setup>
const props = defineProps({
  objetive: {
    type: Object,
    default: () => ({})
  },
  dashboard: {
    type: Boolean,
    default: false,
  },
  darkmode: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['edit', 'setType', 'delete'])

const editObjetive = (data) => emit('edit', data)
const setType = (data, type) => emit('setType', data, type)
const deleteObjetive = (data) => emit('delete', data)

const safeColor = () => {
  const color = props.objetive?.color
  if (!color || typeof color !== 'string') return '#9fb0bf'
  return color
}

const safeIcon = () => props.objetive?.icon || 'fa-bullseye-arrow'
const safeName = () => props.objetive?.name || 'Objetivo sem nome'
const safeId = () => props.objetive?.id ?? ''

const getContrastYIQ = (hexcolor) => {
  if (!hexcolor || typeof hexcolor !== 'string' || !hexcolor.startsWith('#') || hexcolor.length < 7) {
    return 'white'
  }
  const r = parseInt(hexcolor.substring(1, 3), 16)
  const g = parseInt(hexcolor.substring(3, 5), 16)
  const b = parseInt(hexcolor.substring(5, 7), 16)
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000
  return yiq >= 128 ? '#1f2937' : '#ffffff'
}

function formatMoney(v) {
  const total = parseFloat(v || 0)
  return total.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  })
}

function calcPercent(target, balance) {
  const t = parseFloat(target || 0)
  const b = parseFloat(balance || 0)
  if (!t || t <= 0) return 0
  const percent = (b / t) * 100
  return Math.max(0, Math.min(100, percent))
}
</script>

<template>
  <div :id="'objetive' + safeId()" class="col-12 mb-3">
    <div class="objective-card" :class="darkmode ? 'objective-card--dark' : ''">
      <div class="row align-items-center gx-3 gy-3">
        <div class="col-12 col-lg">
          <div class="d-flex align-items-start align-items-lg-center gap-3 flex-column flex-md-row">
            <div class="objective-icon" :style="{ color: objetive.color, backgroundColor: `${objetive.color}20` }">
              <i :class="'fas ' + safeIcon() + ' objective-icon-i'"></i>
            </div>

            <div class="flex-grow-1 w-100 min-w-0">
              <div class="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-2">
                <div>
                  <p class="objective-title mb-1">{{ safeName() }}</p>
                  <p class="objective-subtitle mb-0">
                    {{ formatMoney(objetive?.balance) }} de {{ formatMoney(objetive?.target) }}
                  </p>
                </div>

                <div class="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    class="objective-action-btn"
                    @click="editObjetive(objetive)"
                    data-bs-toggle="modal"
                    data-bs-target="#addObjetiveModal"
                  >
                    <i class="fal fa-pencil"></i>
                  </button>

                  <button
                    type="button"
                    class="objective-action-btn objective-action-btn--danger"
                    @click="deleteObjetive(objetive)"
                  >
                    <i class="fal fa-trash"></i>
                  </button>
                </div>
              </div>

              <div class="row align-items-center g-2 mb-2 objective-meta-row">
                <div class="col-12 col-md-4 text-xs">
                  Saldo<br>
                  <span class="font-weight-bold" :class="darkmode ? 'text-white' : 'text-dark'">
                    {{ formatMoney(objetive?.balance) }}
                  </span>
                </div>
                <div class="col-12 col-md-4 text-xs text-md-center">
                  Progresso<br>
                  <span class="font-weight-bold" :class="darkmode ? 'text-white' : 'text-dark'">
                    {{ Math.round(calcPercent(objetive?.target, objetive?.balance)) }}%
                  </span>
                </div>
                <div class="col-12 col-md-4 text-xs text-md-end">
                  Objetivo<br>
                  <span class="font-weight-bold" :class="darkmode ? 'text-white' : 'text-dark'">
                    {{ formatMoney(objetive?.target) }}
                  </span>
                </div>
              </div>

              <div class="objective-progress">
                <div
                  class="objective-progress-bar"
                  :style="'width:' + calcPercent(objetive?.target, objetive?.balance) + '%; background-color:' + safeColor()"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-auto">
          <div class="d-flex flex-wrap justify-content-lg-end gap-2">
            <button
              type="button"
              data-bs-toggle="modal"
              data-bs-target="#addTransactionObj"
              @click="setType(objetive, 'deposito')"
              class="btn objective-btn objective-btn--success"
            >
              Depositar <i class="fas fa-arrow-circle-down ms-1"></i>
            </button>

            <button
              type="button"
              data-bs-toggle="modal"
              data-bs-target="#addTransactionObj"
              @click="setType(objetive, 'retirada')"
              class="btn objective-btn"
              :class="darkmode ? 'objective-btn--warning' : 'objective-btn--dark'"
            >
              Retirar <i class="fas fa-arrow-circle-up ms-1"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.objective-card {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 22px;
  padding: 20px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
  transition: transform .2s ease, box-shadow .2s ease;
}

.objective-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08);
}

.objective-card--dark {
  background: #162033;
  border-color: rgba(255,255,255,.08);
}

.objective-icon {
  width: 56px;
  height: 56px;
  min-width: 56px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 1px 0 rgba(255,255,255,.2);
}

.objective-icon-i {
  font-size: 24px;
}

.objective-title {
  font-size: 1.08rem;
  font-weight: 700;
  color: #23395b;
}

.objective-card--dark .objective-title {
  color: #f8fafc;
}

.objective-subtitle {
  font-size: .92rem;
  color: #64748b;
}

.objective-card--dark .objective-subtitle {
  color: rgba(255,255,255,.72);
}

.objective-meta-row {
  color: #64748b;
}

.objective-progress {
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: #e8edf3;
  overflow: hidden;
}

.objective-card--dark .objective-progress {
  background: rgba(255,255,255,.1);
}

.objective-progress-bar {
  height: 100%;
  min-width: 6px;
  border-radius: inherit;
  transition: width .35s ease;
}

.objective-action-btn {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: #fff;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all .2s ease;
}

.objective-action-btn:hover {
  color: #23395b;
  border-color: rgba(35,57,91,.2);
}

.objective-action-btn--danger:hover {
  color: #dc2626;
  border-color: rgba(220,38,38,.2);
}

.objective-btn {
  border-radius: 14px;
  padding: 11px 16px;
  font-weight: 700;
  font-size: .92rem;
}

.objective-btn--success {
  border-color: #21c47b;
  color: #1eaf6e;
  background: rgba(33, 196, 123, 0.08);
}

.objective-btn--dark {
  border-color: #23395b;
  color: #23395b;
  background: transparent;
}

.objective-btn--warning {
  border-color: #f59e0b;
  color: #fbbf24;
  background: transparent;
}

.min-w-0 {
  min-width: 0;
}

@media (max-width: 767.98px) {
  .objective-card {
    padding: 16px;
    border-radius: 18px;
  }

  .objective-icon {
    width: 48px;
    height: 48px;
    min-width: 48px;
    border-radius: 14px;
  }

  .objective-btn {
    flex: 1 1 calc(50% - 4px);
  }
}
</style>
